import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  ReactElement,
  forwardRef,
} from 'react';
import { flushSync } from 'react-dom';
import { Box } from '@mui/material';
import { StickyState } from '../../../api/Requests';
import {
  fontForStickyContent,
  MAX_FONT_PX,
  TEXT_FONT_FAMILY,
  TEXT_LINE_HEIGHT,
} from '../../../utils/fitFontSize';
import {
  POINTER_DRAG_THRESHOLD_PX,
  displayZIndex,
  MAX_STICKY_CONTENT_LENGTH,
  clampTextLength,
} from '../../../utils/canvasUtils';
import {
  handleContentEditableBeforeInput,
  truncateContentEditableIfNeeded,
} from '../../../utils/contentEditableMaxLength';
import { DEFAULT_STICKY_CONTENT_BOX, stickyContentBox } from '../../../utils/stickyResize';
import ResizeCornerHandles, { ItemResizeHandleProps } from './ResizeCornerHandles';
import { applyLiveItemPosition, clearLiveItemPosition } from './liveDragPosition';
import ItemOccupancyOverlay from './ItemOccupancyOverlay';

// 6-color palette — curated pastel tones for retro boards.
// Order matches the keyboard shortcuts 1–6 in CanvasSurface.
export const STICKY_COLORS = [
  '#fff9b1', // 1 Yellow  (default)
  '#ffd6e0', // 2 Rose
  '#b8f5e0', // 3 Mint
  '#b8d9ff', // 4 Sky
  '#e0b8ff', // 5 Lavender
  '#ffd9b8', // 6 Peach
];

export interface StickyNoteProps extends ItemResizeHandleProps {
  sticky: StickyState;
  isSelected: boolean;
  /** True when this sticky is part of a multi-sticky selection (2+ selected). */
  isMultiSelected?: boolean;
  onSelect: (id: string) => void;
  onChange: (diff: Partial<StickyState> & { id: string }) => void;
  onDragEnd: (id: string, x: number, y: number) => void;
  onDelete: (id: string) => void;
  /** board-to-screen scale factor (current zoom level) */
  scale: number;
  /** When true, pointer events are swallowed so react-zoom-pan-pinch can pan */
  isPanMode?: boolean;
  /** Called with the sticky id when edit mode starts, null when it ends */
  onEditingChange?: (id: string | null) => void;
  /** Called with the sticky id when a drag begins, null when it ends */
  onDraggingChange?: (id: string | null) => void;
  /** Called whenever the live edit text changes — true if the editor has any content */
  onHasTextChange?: (id: string, hasText: boolean) => void;
  /**
   * Called on every pointer-move frame when this sticky is part of a multi-selection
   * drag. Receives the cumulative board-space delta from the drag origin.
   */
  onMultiDragMove?: (id: string, dx: number, dy: number) => void;
  /**
   * Called on pointer-up when this sticky is part of a multi-selection drag and the
   * pointer genuinely moved (not a click). Receives the final board-space delta.
   */
  onMultiDragEnd?: (id: string, dx: number, dy: number) => void;
  /**
   * When this flips to true the note enters edit mode imperatively.
   * CanvasSurface resets it back to false shortly after setting it.
   */
  shouldStartEdit?: boolean;
  /** Accent color for the selection ring — derived from theme.canvas.selectionColor in CanvasSurface. */
  selectionColor?: string;
  /** Name of another player currently occupying this item — overlay + block gestures. */
  occupiedByName?: string;
  /**
   * Bumped by CanvasSurface when occupancy is denied. Aborts in-flight edit/drag
   * even when the overlay never appears (hub error, deny without another holder).
   */
  occupancyAbortEpoch?: number;
}

const StickyNote = forwardRef<HTMLDivElement, StickyNoteProps>(function StickyNote({
  sticky,
  isSelected,
  isMultiSelected = false,
  onSelect,
  onChange,
  onDragEnd,
  onDelete,
  scale,
  isPanMode = false,
  onEditingChange,
  onDraggingChange,
  onHasTextChange,
  onMultiDragMove,
  onMultiDragEnd,
  shouldStartEdit = false,
  selectionColor = '#2b6be6',
  occupiedByName,
  occupancyAbortEpoch = 0,
  showResizeHandles = false,
  onResizePointerDown,
  onResizePointerMove,
  onResizePointerUp,
  onResizePointerCancel,
  onResizeLostPointerCapture,
}, ref): ReactElement {
  const editRef      = useRef<HTMLDivElement>(null);
  const nodeRef      = useRef<HTMLDivElement | null>(null);
  const dragStartPos = useRef<{ mx: number; my: number; bx: number; by: number } | null>(null);
  const livePosRef   = useRef<{ x: number; y: number } | null>(null);
  const suppressPersistRef = useRef(false);
  const occupancyAbortEpochRef = useRef(occupancyAbortEpoch);
  const [isDragging, setIsDragging] = useState(false); // pointer captured
  const [isMoving,   setIsMoving]   = useState(false); // pointer moved > threshold (visual only)

  const setRootRef = useCallback((el: HTMLDivElement | null) => {
    nodeRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  }, [ref]);
  const [isEditing,  setIsEditing]  = useState(false);
  // Separate font size state for edit mode — updated on every keystroke via handleInput.
  const [editFontSize, setEditFontSize] = useState<number>(MAX_FONT_PX);

  // Resolve display color from slot index so it adapts automatically to theme changes.
  // STICKY_COLORS are light pastels and stay the same in both modes — the background
  // is always readable with dark text regardless of the canvas theme.
  const resolvedColor = STICKY_COLORS[sticky.colorSlot] ?? STICKY_COLORS[0];

  const isSquare = sticky.shape !== 'rectangle';
  const stickyW  = isSquare ? Math.min(sticky.width, sticky.height) : sticky.width;
  const stickyH  = isSquare ? Math.min(sticky.width, sticky.height) : sticky.height;

  // Chrome scales with sticky size so content aspect ratio stays constant on resize.
  const box = stickyContentBox(stickyW);
  const { padding, authorH, authorFont, authorPadTop } = box;
  const contentW = stickyW - padding * 2;

  // Cap content before font-fit / display so oversized persisted notes can't freeze the tab.
  const safeContent = useMemo(
    () => clampTextLength(sticky.content ?? '', MAX_STICKY_CONTENT_LENGTH),
    [sticky.content],
  );

  // Fit at the canonical default content box, then scale to this sticky's width.
  // Same text + size ⇒ same font on remount / other clients (no persisted fontSize).
  const base = DEFAULT_STICKY_CONTENT_BOX;
  const displayFontSize = useMemo(
    () => fontForStickyContent(safeContent, contentW, base.contentW, base.contentH),
    [safeContent, contentW, base.contentW, base.contentH],
  );

  // ── Edit mode ──────────────────────────────────────────────────────────────

  // Shared helper — enters edit mode synchronously (safe to call from event handlers).
  // flushSync ensures the DOM is updated before we set innerText and focus.
  const enterEditMode = useCallback(() => {
    flushSync(() => {
      setEditFontSize(displayFontSize);
      setIsEditing(true);
    });
    onEditingChange?.(sticky.id);
    const el = editRef.current;
    if (!el) return;
    el.innerText = safeContent;
    el.focus();
    const range = document.createRange();
    const sel   = window.getSelection();
    range.selectNodeContents(el);
    range.collapse(false);
    sel?.removeAllRanges();
    sel?.addRange(range);
    onHasTextChange?.(sticky.id, el.innerText.trim().length > 0);
  }, [displayFontSize, onEditingChange, onHasTextChange, safeContent, sticky.id]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (occupiedByName) return;
    enterEditMode();
  }, [enterEditMode, occupiedByName]);

  // Re-fit on every keystroke (canonical box + scale) so blur matches display mode.
  const handleInput = useCallback(() => {
    const el = editRef.current;
    if (!el) return;
    truncateContentEditableIfNeeded(el, MAX_STICKY_CONTENT_LENGTH);
    const text = el.innerText ?? '';
    const newSize = fontForStickyContent(
      text,
      contentW,
      DEFAULT_STICKY_CONTENT_BOX.contentW,
      DEFAULT_STICKY_CONTENT_BOX.contentH,
    );
    setEditFontSize(newSize);
    el.style.fontSize = `${newSize}px`;
    // contenteditable reports "\n" for an empty editor — trim before checking
    onHasTextChange?.(sticky.id, text.trim().length > 0);
  }, [contentW, onHasTextChange, sticky.id]);

  const handleBeforeInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    const el = editRef.current;
    if (!el) return;
    handleContentEditableBeforeInput(e.nativeEvent as InputEvent, el, MAX_STICKY_CONTENT_LENGTH);
  }, []);

  const handleBlur = useCallback(() => {
    if (suppressPersistRef.current) {
      suppressPersistRef.current = false;
      setIsEditing(false);
      onEditingChange?.(null);
      if (editRef.current) editRef.current.style.fontSize = '';
      return;
    }
    setIsEditing(false);
    onEditingChange?.(null);
    // contenteditable always contains a trailing "\n" — trim so empty notes save as ""
    const text = clampTextLength((editRef.current?.innerText ?? '').trim(), MAX_STICKY_CONTENT_LENGTH);
    // handleInput sets style.fontSize imperatively (inline style) to avoid waiting
    // for a React render cycle on every keystroke. Inline styles have higher CSS
    // specificity than the Emotion class generated by the sx prop, so the inline
    // style would override any subsequent sx-driven font size change (e.g. after
    // resize). Clear it here so the sx class takes control again in display mode.
    if (editRef.current) editRef.current.style.fontSize = '';
    onChange({ id: sticky.id, content: text });
  }, [onChange, onEditingChange, sticky.id]);

  // ── Externally triggered edit mode (type-to-edit / Enter / FAB) ────────────
  // When shouldStartEdit flips to true, enter edit mode the same way a
  // double-click would — keeping existing content and placing the cursor at the end.
  // useLayoutEffect (not useEffect) so contentEditable flips on before the browser's
  // first paint — keeps this in sync with TextLabel, which relies on the same timing
  // to avoid a visible size jump when its color strip is positioned off the DOM box.

  useLayoutEffect(() => {
    if (!shouldStartEdit) return;
    setEditFontSize(displayFontSize);
    setIsEditing(true);
    onEditingChange?.(sticky.id);
    // Defer focus/innerText until after React has committed isEditing=true to the DOM,
    // same pattern as double-click — flushSync is not safe inside useLayoutEffect either.
    setTimeout(() => {
      const el = editRef.current;
      if (!el) return;
      el.innerText = safeContent;
      el.focus();
      const range = document.createRange();
      const sel   = window.getSelection();
      range.selectNodeContents(el);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
      onHasTextChange?.(sticky.id, el.innerText.trim().length > 0);
    }, 0);
  }, [shouldStartEdit]); // intentionally omits deps — runs only when shouldStartEdit flips

  // ── Drag ──────────────────────────────────────────────────────────────────

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (isEditing) return;
      // In pan mode, let the event bubble to react-zoom-pan-pinch for canvas panning
      if (isPanMode) return;
      if (occupiedByName) return;
      // Stop both synthetic and native propagation so react-zoom-pan-pinch
      // (which uses native DOM listeners) doesn't start panning during drag.
      e.stopPropagation();
      e.nativeEvent.stopPropagation();
      // When already in a multi-selection, preserve the selection so all selected
      // stickies can be dragged together. A clean click (no drag) will collapse
      // the selection to this single sticky on pointer-up.
      if (!isMultiSelected) {
        onSelect(sticky.id);
      }
      onDraggingChange?.(sticky.id);
      dragStartPos.current = { mx: e.clientX, my: e.clientY, bx: sticky.x, by: sticky.y };
      livePosRef.current = null;
      setIsDragging(true);
      setIsMoving(false);
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
      [isEditing, isMultiSelected, isPanMode, occupiedByName, onDraggingChange, onSelect, sticky.id, sticky.x, sticky.y],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragStartPos.current || !isDragging) return;
      const dx = (e.clientX - dragStartPos.current.mx) / scale;
      const dy = (e.clientY - dragStartPos.current.my) / scale;
      const screenDx = e.clientX - dragStartPos.current.mx;
      const screenDy = e.clientY - dragStartPos.current.my;
      const pastThreshold = isMoving || Math.hypot(screenDx, screenDy) > POINTER_DRAG_THRESHOLD_PX;
      if (!pastThreshold) return;
      // Live position is DOM-imperative (see liveDragPosition.ts). Do not wait
      // for parent onChange to drive React x/y — that re-renders the whole board.
      // Apply before setIsMoving so the lift-effect render cannot snap back to sx.
      const x = dragStartPos.current.bx + dx;
      const y = dragStartPos.current.by + dy;
      livePosRef.current = { x, y };
      applyLiveItemPosition(e.currentTarget as HTMLElement, x, y);
      if (!isMoving) setIsMoving(true);
      if (isMultiSelected && onMultiDragMove) {
        onMultiDragMove(sticky.id, dx, dy);
      } else {
        onChange({ id: sticky.id, x, y });
      }
    },
      [isDragging, isMoving, isMultiSelected, onChange, onMultiDragMove, scale, sticky.id],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!dragStartPos.current || !isDragging) return;
      const startPos = dragStartPos.current;
      const dx     = (e.clientX - startPos.mx) / scale;
      const dy     = (e.clientY - startPos.my) / scale;
      const finalX = startPos.bx + dx;
      const finalY = startPos.by + dy;
      // Detect whether this was a clean click (no movement) using screen-space distance
      const screenDx = e.clientX - startPos.mx;
      const screenDy = e.clientY - startPos.my;
      const wasClick = Math.hypot(screenDx, screenDy) <= POINTER_DRAG_THRESHOLD_PX;
      dragStartPos.current = null;
      livePosRef.current = null;
      setIsDragging(false);
      setIsMoving(false);
      onDraggingChange?.(null);

      if (isMultiSelected) {
        if (wasClick) {
          // Clean click on a multi-selected sticky collapses the selection to just this one
          onSelect(sticky.id);
        } else if (onMultiDragEnd) {
          onMultiDragEnd(sticky.id, dx, dy);
        }
        return;
      }

      if (!wasClick) {
        onDragEnd(sticky.id, finalX, finalY);
        return;
      }
      onDragEnd(sticky.id, finalX, finalY);
      // A clean click on a blank sticky goes straight to edit mode —
      // same behaviour as when a sticky is first created.
      if (!sticky.content.trim()) enterEditMode();
    },
    [enterEditMode, isDragging, isMultiSelected, onDragEnd, onDraggingChange, onMultiDragEnd, onSelect, scale, sticky.content, sticky.id],
  );

  // Re-apply live left/top after isMoving flips (Emotion sx would otherwise
  // snap back to the start x/y from props). Clear after drop so sx owns position.
  useLayoutEffect(() => {
    if (isDragging && livePosRef.current) {
      applyLiveItemPosition(nodeRef.current, livePosRef.current.x, livePosRef.current.y);
      return;
    }
    clearLiveItemPosition(nodeRef.current);
  }, [isDragging, isMoving, sticky.x, sticky.y]);

  // Occupancy deny (or another player taking the lock) arrives as occupiedByName.
  // CanvasSurface also bumps occupancyAbortEpoch when the hub denies without an overlay.
  // Abort in-flight edit/drag locally — CanvasSurface rolls board state back.
  useEffect(() => {
    const epochBumped = occupancyAbortEpoch > 0
      && occupancyAbortEpoch !== occupancyAbortEpochRef.current;
    occupancyAbortEpochRef.current = occupancyAbortEpoch;
    if (!occupiedByName && !epochBumped) return;
    if (isEditing) {
      suppressPersistRef.current = true;
      setIsEditing(false);
      onEditingChange?.(null);
      if (editRef.current) editRef.current.style.fontSize = '';
    }
    if (!isDragging && !isMoving) return;
    dragStartPos.current = null;
    livePosRef.current = null;
    clearLiveItemPosition(nodeRef.current);
    setIsDragging(false);
    setIsMoving(false);
    onDraggingChange?.(null);
  }, [occupiedByName, occupancyAbortEpoch, isEditing, isDragging, isMoving, onDraggingChange, onEditingChange]);

  // ── Keyboard delete ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isSelected || isEditing) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') onDelete(sticky.id);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isSelected, isEditing, onDelete, sticky.id]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const activeFontSize = isEditing ? editFontSize : displayFontSize;

  return (
    <Box
      ref={setRootRef}
      className="sticky-note"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onLostPointerCapture={handlePointerUp}
      onDoubleClick={handleDoubleClick}
      sx={{
        position:       'absolute',
        left:            sticky.x,
        top:             sticky.y,
        width:           stickyW,
        height:          stickyH,
        backgroundColor: resolvedColor,
        borderRadius:   '2px',
        boxShadow:       isSelected
          ? `0 0 0 2px ${selectionColor}, 2px 6px 16px rgba(0,0,0,0.22)`
          : '2px 6px 16px rgba(0,0,0,0.18)',
        cursor:          isMoving ? 'grabbing' : 'grab',
        userSelect:      'none',
        // Visible so relative resize handles (translate -50%) are not clipped.
        // Body content is clipped by the inner absolute fill instead.
        overflow:        'visible',
        boxSizing:       'border-box',
        transform:       isMoving ? 'rotate(2deg) scale(1.03)' : 'none',
        transition:      isMoving ? 'none' : 'box-shadow 0.15s, transform 0.1s',
        zIndex:          displayZIndex(sticky, isMoving),
        touchAction:     'none',
      }}
    >
      {/* Clip chrome + text to the sticky bounds (handles sit outside this).
          Pointer events stay enabled so bubbles reach the outer drag handlers;
          the contenteditable sets pointerEvents itself when editing. */}
      <Box sx={{
        position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: '2px',
        display: 'flex', flexDirection: 'column',
      }}>
      {/* Author label */}
      <Box sx={{
        flexShrink:  0,
        height:      authorH,
        paddingLeft: `${padding}px`,
        paddingTop:  `${authorPadTop}px`,
        fontSize:    authorFont,
        color:       'rgba(0,0,0,0.4)',
        fontFamily:  'sans-serif',
        pointerEvents:'none',
        userSelect:  'none',
        lineHeight:  1,
      }}>
        {sticky.authorName}
      </Box>

      {/* Outer box centres content; contenteditable is on the inner block so the
          browser places the text cursor correctly (flex containers mis-align it). */}
      <Box sx={{
        flex:           1,
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        paddingX:       `${padding}px`,
        paddingBottom:  `${padding}px`,
        overflow:       'hidden',
        cursor:          isEditing ? 'text' : 'inherit',
        pointerEvents:   isEditing ? 'auto' : 'none',
      }}>
        <Box
          ref={editRef}
          contentEditable={isEditing}
          suppressContentEditableWarning
          onBeforeInput={handleBeforeInput}
          onInput={handleInput}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.stopPropagation(); editRef.current?.blur(); }
            if ((e.key === 'Backspace' || e.key === 'Delete') && (editRef.current?.innerText ?? '').trim() === '') {
              e.preventDefault();
              onDelete(sticky.id);
            }
          }}
          sx={{
            width:        '100%',
            // font
            fontSize:     `${activeFontSize}px`,
            fontFamily:   TEXT_FONT_FAMILY,
            fontWeight:   600,
            color:        'rgba(0,0,0,0.85)',
            lineHeight:   TEXT_LINE_HEIGHT,
            textAlign:    'center',
            // wrapping
            wordBreak:    'break-word',
            overflowWrap: 'break-word',
            whiteSpace:   'pre-wrap',
            // editing
            outline:      'none',
            cursor:       'inherit',
          }}
        >
          {!isEditing && safeContent}
        </Box>
      </Box>
      </Box>

      {showResizeHandles && onResizePointerDown && onResizePointerMove && onResizePointerUp && (
        <ResizeCornerHandles
          scale={scale}
          selectionColor={selectionColor}
          onCornerPointerDown={onResizePointerDown}
          onCornerPointerMove={onResizePointerMove}
          onCornerPointerUp={onResizePointerUp}
          onCornerPointerCancel={onResizePointerCancel}
          onCornerLostPointerCapture={onResizeLostPointerCapture}
        />
      )}
      {occupiedByName && <ItemOccupancyOverlay />}
    </Box>
  );
});

export default StickyNote;
